import {isLoggedIn} from '@github-ui/client-env'
import {CodingAssistantButton} from '@github-ui/coding-assistant-button'
import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {useIssueState} from '@github-ui/issue-pr-state'
import {getIssueStateIcon} from '@github-ui/list-view-items-issues-prs/StateIcon'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import {useContainerBreakpoint} from '@github-ui/use-container-breakpoint'
import {InfoIcon, KebabHorizontalIcon, LinkIcon, PlusIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton, SkeletonBox, useMergedRefs} from '@primer/react'
import {clsx} from 'clsx'
import React, {Suspense, useCallback, useEffect, useMemo, useState} from 'react'

import {BUTTON_LABELS} from '../../constants/buttons'
import {TEST_IDS} from '../../constants/test-ids'
import type {IssueSecondaryNode} from '../issue-viewer-secondary-query-types'
import styles from '../IssueViewer.module.css'
import type {OptionConfig} from '../OptionConfig'
import {StickySubscribeButton} from '../sections/subscription-section/StickySubscribeButton'
import {CreateIssueButtonShell} from './CreateIssueButtonShell'
import type {HeaderBlockedBySummaryFragment} from './HeaderBlockedBySummary'
import type {HeaderSubIssueSummaryFragment} from './HeaderSubIssueSummary'
import {useIssueHeaderDataContext} from './IssueHeaderDataContext'
import type {LinkedSecurityAlertsFragment} from './LinkedSecurityAlerts'
import type {
  StickyHeaderIssueTypeNode,
  StickyHeaderRepositoryNode,
  StickyHeaderRepositoryOwnerNode,
} from './sticky-header-query-types'
import {StickyHeaderChips} from './StickyHeaderChips'
import type {TrackedByFragment} from './TrackedBy'
import {ViewPositionIndicator} from './ViewPositionIndicator'

const StickyHeaderCreateIssueButtonLazy = React.lazy(async () => {
  const m = await import('@github-ui/issue-create/CreateIssueButton')
  return {default: m.CreateIssueButton}
})

export type StickyHeaderProps = {
  issueKey: StickyHeaderFragment | null | undefined
  linkedSecurityAlertsData?: LinkedSecurityAlertsFragment
  blockedBySecondaryData?: HeaderBlockedBySummaryFragment
  trackedBySecondaryData?: TrackedByFragment
  subIssueSecondaryData?: HeaderSubIssueSummaryFragment
  issueNumber: number
  ownerLogin: string
  repoName: string
  issueUrl: string
  optionConfig: OptionConfig
  isHeaderSticky: boolean
  onInfoClick: () => void
  infoButtonRef: React.Ref<HTMLButtonElement>
  rootRef?: React.Ref<HTMLDivElement>
}

// RELAY-CLEANUP(IssueViewerSecondaryViewQuery): widens nullable fields so the still-Relay
// parent data is assignable. Remove when IssueViewerSecondaryViewQuery leaves Relay.
export type StickyHeaderFragment = RelayCompat<
  Pick<
    IssueSecondaryNode,
    | 'state'
    | 'stateReason'
    | 'title'
    | 'url'
    | 'subIssuesSummary'
    | 'agentAssignments'
    | 'id'
    | 'threadSubscriptionChannel'
    | 'viewerThreadSubscriptionFormAction'
    | 'closedByPullRequestsReferences'
  > & {
    readonly repository: Pick<StickyHeaderRepositoryNode, 'isWritable' | 'nameWithOwner' | 'name' | 'isPrivate'> & {
      readonly owner: Pick<StickyHeaderRepositoryOwnerNode, 'login' | '__typename'> & {
        readonly name?: string | null
      }
    }
    readonly issueType: Pick<StickyHeaderIssueTypeNode, 'name' | 'color'> | null
  }
>

export function StickyHeader({issueKey, ...rest}: StickyHeaderProps) {
  const sharedHeader = useIssueHeaderDataContext()
  // The sticky header's own data comes from the broad secondary query, which no longer refreshes on
  // a title or state change. Those two fields are taken from the narrow header endpoint instead.
  const issue = useMemo(
    () =>
      issueKey && sharedHeader
        ? {...issueKey, state: sharedHeader.state, stateReason: sharedHeader.stateReason, title: sharedHeader.title}
        : issueKey,
    [issueKey, sharedHeader],
  )
  if (!issue) {
    return <StickyHeaderSkeleton optionConfig={rest.optionConfig} rootRef={rest.rootRef} />
  }
  return <StickyHeaderContent {...rest} issueKey={issue} />
}

function NavControls({optionConfig}: {optionConfig: OptionConfig}) {
  if (!optionConfig.viewNavState) return null
  return (
    <>
      <div className={styles.topContainerNavControls}>
        <ViewPositionIndicator
          navigate={optionConfig.navigate}
          viewNavState={optionConfig.viewNavState}
          onLoadMore={optionConfig.onLoadMoreNavItems}
          onLoadBack={optionConfig.onLoadBackNavItems}
          pendingFocusDirection={optionConfig.viewNavState?.pendingFocusDirection}
          pendingNavSource={optionConfig.viewNavState?.pendingNavSource}
          isStickyHeader
        />
      </div>
      <span aria-hidden="true" className={styles.topContainerExtrasDivider} />
    </>
  )
}

/**
 * In a side sheet with the sticky header on, the panel's nav controls (pin via
 * `additionalHeaderActions`, the kebab from `customEditMenuEntries`, close via `onClose`)
 * are relocated into the sticky top bar and suppressed in `HeaderMenu` to avoid duplicates.
 */
function hasSidePanelNavControls(optionConfig: OptionConfig): boolean {
  if (!optionConfig.insideSidePanel) return false
  const hasCustomMenu = !!optionConfig.customEditMenuEntries && optionConfig.customEditMenuEntries.length > 0
  return !!optionConfig.additionalHeaderActions || hasCustomMenu || !!optionConfig.onClose
}

function SidePanelNavControls({
  optionConfig,
  openInButton,
  infoButton,
  collapsedActions,
}: {
  optionConfig: OptionConfig
  openInButton?: React.ReactNode
  infoButton?: React.ReactNode
  collapsedActions?: React.ReactNode
}) {
  const {additionalHeaderActions, customEditMenuEntries, onClose} = optionConfig
  const hasCustomMenu = !!customEditMenuEntries && customEditMenuEntries.length > 0
  const hasCollapsedActions = !!collapsedActions
  const showKebab = hasCustomMenu || hasCollapsedActions
  const showNavGroup = !!additionalHeaderActions || showKebab
  return (
    <>
      {showNavGroup && (
        <span className={styles.topContainerNavGroup}>
          {additionalHeaderActions}
          {showKebab && (
            <ActionMenu>
              <ActionMenu.Anchor>
                <IconButton
                  icon={KebabHorizontalIcon}
                  aria-label={hasCollapsedActions ? BUTTON_LABELS.moreActions : BUTTON_LABELS.issueActions}
                  variant="invisible"
                  size="medium"
                />
              </ActionMenu.Anchor>
              <ActionMenu.Overlay>
                <ActionList>
                  {collapsedActions}
                  {hasCollapsedActions && hasCustomMenu && <ActionList.Divider />}
                  {hasCustomMenu && customEditMenuEntries.map(e => e)}
                </ActionList>
              </ActionMenu.Overlay>
            </ActionMenu>
          )}
        </span>
      )}
      {openInButton}
      {infoButton}
      {onClose && (
        <IconButton
          icon={XIcon}
          aria-label={BUTTON_LABELS.closePanel}
          variant="invisible"
          size="medium"
          onClick={onClose}
        />
      )}
    </>
  )
}

function StickyHeaderSkeleton({
  optionConfig,
  rootRef,
}: {
  optionConfig: OptionConfig
  rootRef?: React.Ref<HTMLDivElement>
}) {
  const showSidePanelNav = hasSidePanelNavControls(optionConfig)
  return (
    <div
      ref={rootRef}
      data-testid={TEST_IDS.issueViewerTopContainer}
      className={clsx(styles.topContainer, showSidePanelNav && styles.topContainerSidePanel)}
    >
      <div className={styles.topContainerLeading}>
        <NavControls optionConfig={optionConfig} />
        <SkeletonBox width="180px" height="20px" />
      </div>
      <div className={styles.topContainerExtras}>
        <div className={styles.topContainerActionGroup} aria-hidden="true">
          <SkeletonBox width="24px" height="24px" />
          <SkeletonBox width="24px" height="24px" />
          <SkeletonBox width="24px" height="24px" />
        </div>
        {showSidePanelNav && (
          <div className={styles.topContainerActions}>
            <span aria-hidden="true" className={styles.topContainerActionsDivider} />
            <div className={styles.topContainerPrimaryCluster}>
              <SidePanelNavControls optionConfig={optionConfig} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

type StickyHeaderContentProps = StickyHeaderProps & {issueKey: StickyHeaderFragment}

function StickyHeaderContent({
  issueKey: data,
  linkedSecurityAlertsData,
  blockedBySecondaryData,
  trackedBySecondaryData,
  subIssueSecondaryData,
  issueNumber,
  ownerLogin,
  repoName,
  issueUrl,
  optionConfig,
  isHeaderSticky,
  onInfoClick,
  infoButtonRef,
  rootRef,
}: StickyHeaderContentProps) {
  const {stateString} = useIssueState({
    state: data.state,
    stateReason: data.stateReason,
    options: {longText: false},
  })
  const stateOrReason =
    data.state === 'CLOSED' && (data.stateReason === 'NOT_PLANNED' || data.stateReason === 'DUPLICATE')
      ? data.stateReason
      : data.state
  const {icon: StateIcon, color: stateIconColor} = getIssueStateIcon(stateOrReason)

  // At narrow viewports collapse the bell/copy/new-issue actions into a single
  // kebab menu; above the breakpoint we keep the three individual buttons.
  const [isNarrow, setIsNarrow] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
    const mql = window.matchMedia('(max-width: 768px)')
    const update = () => setIsNarrow(mql.matches)
    update()
    mql.addEventListener('change', update)
    return () => mql.removeEventListener('change', update)
  }, [])

  // In side-panel mode the panel can be narrow while the viewport is wide, so the media query
  // never fires; collapse on the header's own width instead. `[true,true,false,false]`
  // (sm/md/lg/xl) is true while the panel is below 768px (ScreenSize.large).
  const [topContainerEl, setTopContainerEl] = useState<HTMLDivElement | null>(null)
  const mergedRootRef = useMergedRefs(setTopContainerEl, rootRef ?? null)
  const containerBreakpoint = useContainerBreakpoint(topContainerEl)
  const isContainerNarrow = containerBreakpoint([true, true, false, false]) ?? false

  const showCreateButton = optionConfig.showIssueCreateButton && data.repository.isWritable
  const showSidePanelNav = hasSidePanelNavControls(optionConfig)

  const openInButton = (
    <CodingAssistantButton
      url={issueUrl}
      buttonLabel={BUTTON_LABELS.openIn}
      promptBuilder={(url: string) => `Work on the following GitHub issue: ${url}.`}
      size="medium"
      variant="primary"
      iconOnly
    />
  )
  // In side-panel mode the "Open in" button is relocated to sit just left of the close button.
  const relocateOpenIn = showSidePanelNav

  const infoButton = (
    <span
      className={clsx(
        styles.sidebarInfoButton,
        optionConfig.useViewportQueries ? styles.sidebarInfoButton_viewport : styles.sidebarInfoButton_container,
      )}
    >
      <IconButton
        ref={infoButtonRef}
        icon={InfoIcon}
        aria-label="Show issue details"
        variant="default"
        size="medium"
        onClick={onInfoClick}
      />
    </span>
  )

  const handleCopyLink = useCallback(() => {
    if (typeof navigator !== 'undefined' && navigator.clipboard) {
      void navigator.clipboard.writeText(issueUrl)
    }
  }, [issueUrl])

  // Full-page collapses on viewport width; side-panel collapses on the panel's container width.
  const actionsCollapsed = optionConfig.useViewportQueries ? isNarrow : isContainerNarrow

  // The bell/copy/new actions as ActionList items, shared by the full-page overflow kebab
  // and the side-panel nav kebab (subscribe/copy-link collapse in; the pin stays inline).
  const collapsedActionItems = (
    <>
      {isLoggedIn() && (
        <StickySubscribeButton
          subscriptionData={data}
          owner={ownerLogin}
          repo={repoName}
          number={issueNumber}
          variant="menu-item"
        />
      )}
      <ActionList.Item onSelect={handleCopyLink}>
        <ActionList.LeadingVisual>
          <LinkIcon />
        </ActionList.LeadingVisual>
        {BUTTON_LABELS.copyIssueLink}
      </ActionList.Item>
      {showCreateButton && (
        <ActionList.LinkItem href={`/${ownerLogin}/${repoName}/issues/new`}>
          <ActionList.LeadingVisual>
            <PlusIcon />
          </ActionList.LeadingVisual>
          {BUTTON_LABELS.new}
        </ActionList.LinkItem>
      )}
    </>
  )

  return (
    <div
      ref={mergedRootRef}
      data-testid={TEST_IDS.issueViewerTopContainer}
      className={clsx(styles.topContainer, showSidePanelNav && styles.topContainerSidePanel)}
    >
      <div className={styles.topContainerLeading}>
        <NavControls optionConfig={optionConfig} />
        <span style={{color: stateIconColor, display: 'inline-flex'}}>
          <StateIcon aria-label={stateString} />
        </span>
        <a href="#top" className={styles.topContainerTitle}>
          {data.title}
        </a>
        <span className={styles.topContainerIssueNumber}>#{issueNumber}</span>
        <div
          className={clsx(styles.topContainerChipsSlot, !isHeaderSticky && styles.topContainerChipsSlotHidden)}
          aria-hidden={!isHeaderSticky}
        >
          <StickyHeaderChips
            issueData={data}
            state={data.state}
            linkedSecurityAlertsData={linkedSecurityAlertsData}
            blockedBySecondaryData={blockedBySecondaryData}
            trackedBySecondaryData={trackedBySecondaryData}
            subIssueSecondaryData={subIssueSecondaryData}
            optionConfig={optionConfig}
          />
        </div>
      </div>
      <div className={styles.topContainerExtras}>
        {actionsCollapsed ? (
          // In a side panel these move into the nav kebab (SidePanelNavControls), so render
          // nothing here; full-page surfaces use a standalone overflow kebab.
          showSidePanelNav ? null : isLoggedIn() ? (
            <span className={clsx(styles.topContainerOverflowSlot, styles.topContainerMoreActionsSlot)}>
              <ActionMenu>
                <ActionMenu.Anchor>
                  <IconButton
                    icon={KebabHorizontalIcon}
                    aria-label={BUTTON_LABELS.moreActions}
                    variant="invisible"
                    size="medium"
                  />
                </ActionMenu.Anchor>
                <ActionMenu.Overlay>
                  <ActionList>{collapsedActionItems}</ActionList>
                </ActionMenu.Overlay>
              </ActionMenu>
            </span>
          ) : (
            <span className={styles.topContainerOverflowSlot}>
              <CopyToClipboardIconButton
                textToCopy={issueUrl}
                ariaLabel={BUTTON_LABELS.copyIssueLink}
                icon={LinkIcon}
                variant="invisible"
                tooltipProps={{direction: 's'}}
              />
            </span>
          )
        ) : (
          <div className={styles.topContainerActionGroup}>
            {isLoggedIn() && (
              <span className={styles.topContainerOverflowSlot}>
                <StickySubscribeButton
                  subscriptionData={data}
                  owner={ownerLogin}
                  repo={repoName}
                  number={issueNumber}
                />
              </span>
            )}
            <span className={styles.topContainerOverflowSlot}>
              <CopyToClipboardIconButton
                textToCopy={issueUrl}
                ariaLabel={BUTTON_LABELS.copyIssueLink}
                icon={LinkIcon}
                variant="invisible"
                tooltipProps={{direction: 's'}}
              />
            </span>
            {showCreateButton && (
              <span className={clsx(styles.topContainerOverflowSlot, styles.topContainerNewSlot)}>
                <Suspense fallback={<CreateIssueButtonShell iconOnly />}>
                  <StickyHeaderCreateIssueButtonLazy
                    label={BUTTON_LABELS.new}
                    variant="invisible"
                    iconOnly
                    leadingVisual={PlusIcon}
                    navigate={optionConfig.navigate}
                    optionConfig={{
                      showRepositoryPicker: false,
                      issueCreateArguments: {
                        repository: {
                          owner: ownerLogin,
                          name: repoName,
                        },
                      },
                      showFullScreenButton: true,
                      navigateToFullScreenOnTemplateChoice: true,
                      canBypassTemplateSelection: true,
                      navigate: optionConfig.navigate,
                    }}
                  />
                </Suspense>
              </span>
            )}
          </div>
        )}
        <div className={styles.topContainerActions}>
          {!actionsCollapsed && <span aria-hidden="true" className={styles.topContainerActionsDivider} />}
          <div className={styles.topContainerPrimaryCluster}>
            {!relocateOpenIn && openInButton}
            {/* In side-panel mode the info button moves into the nav cluster, just after the
                relocated "Open in" button; otherwise it renders inline here. */}
            {!showSidePanelNav && infoButton}
            {showSidePanelNav && (
              <SidePanelNavControls
                optionConfig={optionConfig}
                openInButton={relocateOpenIn ? openInButton : undefined}
                infoButton={infoButton}
                collapsedActions={actionsCollapsed ? collapsedActionItems : undefined}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
